<template>
  <div id="notes" :class="{'teacher-notes': teacherNotes, 'processing': isProcessingData}">
    <v-layout pa-2 class='notes-actions'>
      <div :style="notesActionsStyle">
        <v-btn v-if="sectionPreview" :size="14" flat small icon class="mx-0 my-1" @click="getAttemptNotes" aria-label="Refresh Notes" color="grey darken-1">
          <v-icon>refresh</v-icon>
        </v-btn>
        <v-btn :size="14" flat small icon class="mx-0 my-1" @click="downloadNotes" label="download notes" aria-label="Download Notes as Text File" color="grey darken-1" v-if="!isAndroid">
          <v-icon>file_download</v-icon>
        </v-btn>
        <v-btn :size="14" flat small icon class="mx-0 my-1" @click="toggleSearch" :aria-label="(showSearch ? 'Close' : 'Open') +  'Notes Search Field'" color="grey darken-1">
          <v-icon>search</v-icon>
        </v-btn>
      </div>
      <v-flex xs5 mx-2>
        <!--single-line-->
        <transition name="squish">
          <v-text-field
            class='notes-search ma-0 pa-0'
            name="text-search"
            label="Search notes"
            placeholder="Search Notes"
            ref="searchNotes"
            v-model="searchText"
            v-show="showSearch"
            hide-details
          ></v-text-field>
        </transition>
      </v-flex>
      <v-flex xs5 v-if="sectionPreview" class="mr-1">
        <b-form-select
          aria-label="Filter Notes by Learner"
          box
          class="full-width full-height"
          v-model="filterByAuthorId"
          >
          <option v-for="(author, i) in authors" :value="author.value" >{{author.text}}</option>
        </b-form-select>
      </v-flex>
    </v-layout>
    <div id="notes-wrapper" ref='notesWrapper'>
      <!--<student-notes></student-notes>-->
      <notes-list v-if="sectionPreview" :notes='filteredNotes' :grouped="true"></notes-list>
      <v-list v-else>
        <template v-for="note in filteredNotes">
          <v-container :key="note.id" px-2 pt-2 grid-list-md class="discussion-comment" :id="'note-' + note.id">
            <v-layout row px-2>
              <v-flex xs2>
                <v-layout column align-center>
                  <avatar :size="36" :username="user.firstName + ' ' + user.lastName"></avatar>
                  <span class="caption comment-timestamp" @click="jump(note.displayTime)" @keyup.enter="jump(note.displayTime)" tabindex="0"  :aria-label="formatedDuration(note.displayTime) + ' Click to jump to note time'" role="button">{{formatedDuration(note.displayTime)}}</span>
                </v-layout>
              </v-flex>
              <v-flex xs10 class="right">
                <h3 class="comment-name">
                  <strong :aria-label="'Note by ' + user.firstName + ' ' + user.lastName + ' created ' + fromNow(note.created)">
                    {{user.firstName + " " + user.lastName}}
                    <span class="caption">- {{fromNow(note.created)}}</span>
                  </strong>
                </h3>
                <p class="comment-content" v-html="note.text"></p>
                <div class="comment-actions">
                  <span @click="toggleEditNote(note.id)" @keyup.enter="toggleEditNote(note.id)" tabindex="0" aria-label="Open Note Editor" role="button">Edit</span>
                  <span @click="deleteNote(note.id)" @keyup.enter="deleteNote(note.id)" tabindex="0" aria-label="delete note" role="button">Delete</span>
                </div>
              </v-flex>
            </v-layout>
          </v-container>
        </template>
      </v-list>
        <!--<div class="more-messages-below" v-if="!inAutoScrollRange && scrollbar && scrollbar.scrollerHeight > scrollbar.wrapperHeight && notes.length > 0" @click="scrollToBottom">More messages below. </div>-->
      <div class="loading-screen" v-if="isProcessingData">
        <v-progress-circular indeterminate v-bind:size="50" class='branding-active-text'></v-progress-circular>
      </div>
    </div> <!-- end .notes-wrapper-->
     <v-layout row grow pa-3 white style="max-height: 90%;" v-if="user.userType === 'student' || notesPreview">
        <v-flex xs11 align-content-center>
          <interaction-tinymce
            @tinymce-input="handleTinymceInput"
            disabledButtons="['video']"
            ariaLabel="Input field for notes text."
            :inline="true"
            :existing-text="noteInput"
            tinymce-key="notes"
            ref="tinymce"
            :placeholderText="notesPlaceholder"
            font-size-class="pp-interaction-title"
          ></interaction-tinymce>
          <span id="instructor-note-text" class="caption" v-if="user.userType === 'student'">Instructors may view notes.</span>
          <span class="caption" v-if="user.userType !== 'student'">Notes are not saved.</span>
        </v-flex >
        <v-flex x1 row align-content-center>
          <v-btn :disabled="noteInput.length === 0" flat icon small :color="branding.activeColor" @click="createNote" v-if="['post'].includes(mode.type)" aria-label='create new note'>
            <v-icon>send</v-icon>
          </v-btn>
          <v-btn :disabled="noteInput.length === 0" flat icon small :color="branding.activeColor" @click="updateNote" v-if="['edit'].includes(mode.type)" aria-label='update note'>
            <v-icon>send</v-icon>
          </v-btn>
        </v-flex>
    </v-layout>
  </div> <!-- end #note -->
</template>

<script>
/* global tinymce */
/* global _ */
/* global Blob */
/* global dataLayer */
var FileSaver = require('file-saver');
var moment = require('moment');
import { mapGetters, mapMutations, mapState } from 'vuex';
import Avatar from 'vue-avatar';
import IScroll from 'iscroll/build/iscroll-probe.js';
import InteractionTinymce from '@/components/interaction/InteractionTinymce';
import NotesList from './NotesList';
import PlayerService from '@/services/PlayerService';
import XApiService from '@/services/XApiService';
import EventBus from '@/buses/EventBus';
const store = require('store');

export default {
  name: 'notes',
  components: {
    Avatar,
    InteractionTinymce,
    NotesList,
  },
  props: [ ],
  data () {
    return {
      filterByAuthorId: null,
      isProcessingData: false,
      inAutoScrollRange: false,
      lastFocus: null,
      mode: { // post/replay/edit to know how to handle the tinymce submit request
        type: 'post',
        id: null
      },
      notes: [],
      noteInput: '',
      notesLoaded: false,
      previousAutoScrollTime: null,
      scrollbar: null,
      searchText: '',
      showSearch: false,
      notesFormArray: [true],
      showNotesPrivacyWarning: true,
    }
  },
  computed: {
    ...mapGetters('player', [
      'branding',
      'bulb',
      'bulbAttempt',
      'displayTime',
      'isBarbri',
      'mobile',
  		'playerState',
      'sidebarTab',
      'sidebarOpen',
      'showInteractionsTab',
      'user',
      'userAgent',
      'termForBulb',
    ]),
    ...mapState('player', [
      'formatedDuration',
      'isLiveSession',
    ]),
    authors() {
      const users =  _.sortBy(
          _.uniqBy(
            _.without(
              _.map(
                this.notes, (note) => {
                  if (note.userId > 0) return {value:note.userId, text: `${note.lastName}, ${note.firstName}` };
                }),
              undefined),
            'value'),
          'text');
      users.unshift({value: null, text: 'All Learners'});
      return users;
    },
    filteredNotes() {
      const regex = new RegExp('(' + this.searchText + ')', 'i');
      return _.orderBy(this.notes.filter((n) => {
        if (this.filterByAuthorId && this.searchText) return n.text.match(regex) && n.userId === this.filterByAuthorId;
        if (this.filterByAuthorId) return n.userId === this.filterByAuthorId;
        if (this.searchText) return n.text.match(regex)
        return true;
      }), 'displayTime')
    },
    sectionPreview() {
      return this.user.userType === 'teacher' && !this.notesPreview;
    },
    notesActionsStyle() {
       return this.sectionPreview ? 'min-width: 100px' : '';
    },
    notesPreview() {
      if (!this.user) return true;
      if (this.user.userType === 'student') return false;
      if (this.user.userType === 'teacher' && this.bulbAttempt.sectionId) return false;
      return true;
    },
    notesPlaceholder(){
      if (this.user.userType === 'student') return `Saving note as ${this.user.firstName} ${this.user.lastName}`;
      else if (this.user.userType === 'teacher') return `Composing as ${this.user.firstName} ${this.user.lastName}`;
      else return `Acting as ${this.user.firstName} ${this.user.lastName}`;
    },
    showNotesForm:{
      get(){
        return this.notesFormArray[0]
      },
      set(val) {
        this.notesFormArray = [val]
      }
    },
    teacherNotes() {
      return (this.user.user_type === 'teacher');
    },
    isAndroid() {
      if (this.userAgent.os.name === 'Android') return true;
      return false;
    },
    visible() {
      return this.sidebarTab === 'notes-tab' && this.sidebarOpen && !this.showInteractionsTab;
    },
    hasKeepPlayingWhileTakingNotesFeature() {
      return _.get(this.bulb, 'masterTeacher.school.config.permissions', []).includes('hasKeepPlayingWhileTakingNotesFeature');
    },
  },
  methods: {
    ...mapMutations('player', [
      'setDesiredState',
      'setManualJump',
      'setSnackbar',
    ]),
    calculateAutoScroll() {
      // checks to see if you're in the bottom of scroll area to enable autoscrolling to new content
      // this.inAutoScrollRange = (this.scrollbar && this.scrollbar.scrollerHeight > this.scrollbar.wrapperHeight && this.scrollbar.y < this.scrollbar.maxScrollY * 0.85);
    },
    createNote () {
      if (this.notesPreview) {
        // notes preview create note
        const noteId =  moment();
        this.notes.push({
          id: new Date().getTime(),
          bulbAttemptId: 0,
          text: this.noteInput,
          displayTime: this.displayTime,
          archived: "2100-12-01T00:00:00.000Z",
          created: noteId,
          updated: "2018-12-14T18:52:33.000Z",
        });
        this.resetMode(this.$refs.tinymce.editor);
        this.$nextTick(() => {
          this.setSnackbar({
  					text:'Note created!',
  		  		color: 'success',
  				});
        });
        return;
      }
      this.isProcessingData = true;
      PlayerService.createNote({
          bulbAttemptId: this.bulbAttempt.id,
          text: this.noteInput,
          displayTime: this.displayTime,
      })
      .then((response) => {
        if (response.data.msg === 'success') {
          this.setSnackbar({
  					text:'Note created!',
  		  		color: 'success',
  				});
          this.noteInput = '';
          this.notes.push(response.data.note);
      //       var statement = {
      //         activity: {
      //           id: `https://xapi.playposit.com/activities/comment`,
      //           name: `${response.data.data.id}`,
      //           discussion_id: Number(response.data.data.discussion_id)
      //         },
      //       };
      //       XApiService.buildPostDiscussionCommentStatement(statement);
      //     }
          this.resetMode(this.$refs.tinymce.editor);
          // this.$nextTick(() => {
            // document.getElementById(`#note-${response.data.note.id}`).scrollIntoView();
          // })
        }
        this.showNotesForm = false;
        this.isProcessingData = false;
      })
      .catch((err) => {
        this.isProcessingData = false;
      })
    },
    deleteNote(noteId) {
      if (this.notesPreview) {
        this.notes.splice(_.findIndex(this.notes, {id: noteId}), 1);
        if (noteId === this.mode.id && this.mode.type === 'edit') {
          // deleted a note that you were editing
          this.toggleEditNote(null, false);
        }
        return;
      }
      this.isProcessingData = true;
      PlayerService.deleteNote(noteId)
      .then((response) => {
        if (response.data.msg === 'success') {
          this.notes = this.notes.filter(n => n.id !== noteId);
          if (this.inAutoScrollRange) this.$nextTick(() => this.scrollToBottom())
          if (noteId === this.mode.id && this.mode.type === 'edit') {
            // deleted a note that you were editing
            this.toggleEditNote(null, false);
          }
          this.isProcessingData = false;
        }
      })
      .catch((err) => {
        this.isProcessingData = false;
      })
    },
    downloadNotes() {
      var html = '';
      this.notes.forEach((c, i) => {
        c.text = c.text.replace(/\<(?!img|audio).*?\>/g, "");
        html += `(${this.formatedDuration(c.displayTime)}) - ` + c.text + '\r\n\r\n';
      });
      var blob = new Blob([html], {type: 'text/plain;charset=utf-8'});
      FileSaver.saveAs(blob, 'playposit-notes.txt');
      dataLayer.manualLog(`Player: notes downloaded - schoolId ${_.get(this.bulb, 'masterTeacher.schoolId', 'no schoolId')}`, 'noteDownload');
    },
    toggleEditNote(noteId, open = true){
      if (noteId) {
        this.lastFocus = document.getElementById(`note-${noteId}`);
        this.mode = { type: 'edit', id: noteId}
        this.noteInput = _.find(this.notes, {id: noteId}).text
        this.$nextTick(() => EventBus.$emit('applyExistingText', 'notes'));
        this.showNotesForm = true;
      }
      else{
        this.mode = {type: 'post', id: 0}
        this.noteInput = '';
        this.$nextTick(() => {
          EventBus.$emit('applyExistingText', 'notes');
        })
        this.showNotesForm = open
      }
    },
    toggleSearch() {
      this.showSearch = !this.showSearch;
      if (this.showSearch) setTimeout(() => {this.$refs.searchNotes.focus();}, 401);
    },
    fromNow (time) {
      return moment(time).fromNow();
    },
    getAttemptNotes() {
      if (this.notesPreview) {
        this.notesLoaded = true;
        return
      }
      const notesService = this.user.userType === 'student' ?
        PlayerService.getAttemptNotes(this.bulbAttempt.id) :
        PlayerService.getSectionNotes(this.bulb.id, this.bulbAttempt.sectionId);

      this.isProcessingData = true;
      notesService
      .then((response) => {
        this.notes = response.data;
        if (this.inAutoScrollRange) {
          this.scrollToBottom();
        }
        this.isProcessingData = false;
        this.notesLoaded = true;
      })
      .catch((err) => {
        this.isProcessingData = false;
      })
    },
    handleTinymceInput(input) {
      // handler for anything that gets typed into the generated tinyMCE component
      if (this.isBarbri && this.hasKeepPlayingWhileTakingNotesFeature) this.noteInput = input;
      if (this.playerState === 'play' && input && input !== this.noteInput && !this.isLiveSession) EventBus.$emit('requestAction', 'pause');
      this.noteInput = input;
    },
    jump(displayTime) {
      // [ ] move to vuex
      if (this.isLiveSession) {
        this.setSnackbar({
          color: 'warning',
          text: `Jumping is disabled for session ${this.termForBulb}s.`,
          timeout: 5000,
        });
        return;
      }
      this.setDesiredState('pause');
      EventBus.$emit('requestAction', {
        key: 'seek',
        seekTime: displayTime,
        bypassGates: ['notWithinBounds', 'alreadySeeking']
      });
    },
    resetMode(focusEl) {
      this.toggleEditNote(null, false);
      if (focusEl) focusEl.focus();
      else this.$refs.searchNotes.focus();
      this.$refs.tinymce.clear();
    },
    updateNote() {
      if (this.notesPreview) {
        this.notes[_.findIndex(this.notes, {'id': this.mode.id})].text = this.noteInput
        this.resetMode(this.$refs.tinymce.editor);
        return
      }
      this.isProcessingData = true;
      PlayerService.updateNote(this.mode.id, {text: this.noteInput})
      .then((response) => {
        this.isProcessingData = false;
        if (response.data.msg == 'success') {
          const index = _.findIndex(this.notes, {id: response.data.note.id});
          this.notes.splice(index, 1, response.data.note);
          this.resetMode(this.$refs.tinymce.editor);
            this.$nextTick(() => {
              // document.getElementById(`#note-${this.mode.id}`).scrollIntoView();
            });
        }
        // var statement = {
        //   activity: {
        //     id: `https://xapi.playposit.com/activities/comment`,
        //     name: `${response.data.data.id}`,
        //     discussion_id: Number(response.data.data.discussion_id)
        //   },
        // };
        // XApiService.buildEditDiscussionCommentStatement(statement);
        this.toggleEditNote(0, false);
        this.isProcessingData = false;
      })
      .catch((err) => {
        this.isProcessingData = false;
      })
    },
    scrollToBottom() {
      setTimeout(() => {
        // this.$refs.notesWrapper.scrollIntoView(false);
        this.calculateAutoScroll();
      }, 0);
    },
    autoScroll() {
      this.previousAutoScrollTime = this.$store.state.display_time;
      var commentTimes = this.notes.map(a => a.display_time);
      var closestTime = commentTimes.sort( (a, b) => Math.abs(this.previousAutoScrollTime - a) - Math.abs(this.previousAutoScrollTime - b) )[0];
      var closestComment = _.find(this.notes, {display_time: closestTime});
      if (closestComment) {
        // document.getElementById(`#note-${closestComment.id}`).scrollIntoView();;
      }
      this.calculateAutoScroll();
    },
    updateUserComment(msg) {
      this.noteInput = msg;
    },

  },
  watch: {
    'showNotesForm'(){
      setTimeout(() => {
        if (this.showNotesForm) this.$refs.tinymce.editor.focus();
      }, 310);
    },
    'visible'() {
      if (this.visible) {
        if (this.user.userType !== 'teacher') this.$refs.tinymce.editor.focus();
      }
    }
  },
  mounted() {
    // this.scrollbar.on('scroll', () => {
    //   this.calculateAutoScroll();
    // });
    this.getAttemptNotes();

    // this.$store.watch(
    //   (state)=>{
    //     return this.$store.state.display_time
    //   },
    //   (newVal, oldVal)=>{
    //     var notes = document.querySelector(`.notes-${this.question.id}`);
    //     if (!notes) return;
    //     var mouseOver = (comments.parentElement.querySelector(':hover') === comments);
    //     if (this.previousAutoScrollTime === null || this.previousAutoScrollTime === undefined) {
    //       this.previousAutoScrollTime = newVal - 5;
    //     }
    //     if (!mouseOver && newVal !== oldVal && newVal - this.previousAutoScrollTime >= 5) {
    //       this.autoScroll();
    //     }
    //   }
    // );
  }
};
</script>

<style>

  #notes {
    display:flex;
    flex-direction: column;
    min-height: 0;
    flex-grow: 1;
  }

  #EqnEditor .toolbar {
    box-shadow: none !important;
  }
  h3.comment-name {
    font-size: 14px;
    line-height: 1.1;
    margin: 0;
    font-weight: 700;
  }

  /* */
  #notes-wrapper {
    position: relative;
    flex-grow: 100;
  }

  #notes {
    height: 100%;
    border-radius: 2px;
    background: #fff;
    position: relative;
  }

  .notes-actions {
    min-height: 50px;
  }

  .notes .list {
    background: transparent;
  }

  .notes .notes-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .notes-search label{
    padding: 18px 0 0;
    margin-top: -35px;
  }

  .loading-screen {
    z-index: 999999999;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    pointer-events: none;
    background-color: rgba(255,255,255,0.5);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .notes-form .form-actions {
    display: flex;
  }

  .notes-form .form-header {
    user-select: none;
    text-transform: uppercase;
    font-weight: 500;
    white-space: normal;
  }
  .squish-enter-active, .squish-leave-active {
    transition: width .3s;
  }
 .squish-enter {
    width: 0;
  }
  .squish-enter-to {
    width:100%;
  }
  .squish-leave {
    width: 100%
  }
  .squish-leave-to {
    width: 0;
  }

</style>
